import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { oidSchema } from '@zaoblako/shared/modules/serde/schemas/oid.schema';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';

import * as yup from 'yup';

/** Default device connection UDP port number */
export const DEFAULT_UDP_PORT = 161;

/** Default delay between attempts to open the barrier in seconds */
export const DEFAULT_BARRIER_OPEN_ATTEMPT_DELAY = 1;

/** Default number of attempts to open the barrier */
export const DEFAULT_BARRIER_OPEN_ATTEMPTS_COUNT = 15;

/** "NetPing" device create and update APIs request body - DTO signature */
export interface NetPingDeviceFormInterface {
  /** Device display name */
  name: string;
  /** Device IP-address */
  ipAddress: string;
  /** Device connection UDP port number ({@link DEFAULT_UDP_PORT} by default) */
  udpPort?: number;
  /** Community line for the barrier opening channel */
  barrierCommunity: string;
  /** OID of the channel through which the barrier is opened */
  barrierOpenRelayOid: string;
  /** The value that must be sent to the channel to open the barrier */
  barrierOpenRelayValue: number;
  /** OID of the channel through which the barrier status is read */
  barrierStatusRelayOid: string;
  /** The value at which the barrier should be considered open */
  barrierStatusRelayOpenValue: number;
  /** Open only a closed barrier */
  barrierOpenOnlyClosed: boolean;
  /** Delay between attempts to open the barrier in seconds ({@link DEFAULT_BARRIER_OPEN_ATTEMPT_DELAY} by default) */
  barrierOpenAttemptDelay?: number;
  /** Number of attempts to open the barrier ({@link DEFAULT_BARRIER_OPEN_ATTEMPTS_COUNT} by default) */
  barrierOpenAttemptsCount?: number;
  /** The room that is access controlled by this device */
  room?: RoomFindInterface;
}

/** "NetPing" device create and update APIs request body - DTO schema */
export const NetPingDeviceFormDto = SerdeDtoClass.create<NetPingDeviceFormInterface>(PacsDtoKeysEnum.NET_PING_DEVICE_FORM_DTO, {
  name: {
    sortable: true,
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  ipAddress: {
    sortable: true,
    label: 'IP-адрес устройства',
    serializer: nonEmptyStringSchema,
  },
  udpPort: {
    optional: true,
    label: 'UDP-порт подключения устройства',
    serializer: idLikeSchema,
  },
  barrierCommunity: {
    label: 'Community-строка устройства',
    serializer: nonEmptyStringSchema,
  },
  barrierOpenRelayOid: {
    label: 'OID канала, по которому открывается шлагбаум',
    serializer: oidSchema,
  },
  barrierOpenRelayValue: {
    label: 'Значение, которое надо отправить в канал для открытия шлагбаума',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE)
      .integer(DtoMessageEnum.NOT_INTEGER),
  },
  barrierStatusRelayOid: {
    label: 'OID канала, по которому считывается состояние шлагбаума',
    serializer: oidSchema,
  },
  barrierStatusRelayOpenValue: {
    label: 'Значение, при котором шлагбаум следует считать открытым',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE)
      .integer(DtoMessageEnum.NOT_INTEGER),
  },
  barrierOpenOnlyClosed: {
    label: 'Открывать только закрытый шлагбаум',
    hint: (
      'Если включено, то перед отправкой сигнала на открытие будет проверяться, не открыт ли уже шлагбаум. ' +
      'Если шлагбаум открыт, то ФРМ будет ждать, пока шлагбаум не закроется или пока не закончатся попытки открыть шлагбаум.'
    ),
    serializer: yup.boolean(),
  },
  barrierOpenAttemptDelay: {
    optional: true,
    label: 'Интервал между попытками открыть шлагбаум в секундах',
    serializer: idLikeSchema,
  },
  barrierOpenAttemptsCount: {
    optional: true,
    label: 'Максимальное количество попыток открыть шлагбаум',
    serializer: idLikeSchema,
  },
  room: {
    optional: true,
    label: 'Помещение',
    hint: 'Помещение, доступ в которое контролируется этим устройством',
    serializer: entityLikeSchema,
  },
});
